import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, Check, ExternalLink, Brain, Shield, FileCheck, Package } from 'lucide-react';
import { api } from '../api/client';

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications/inbox/latest?limit=5');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.debug('Notification fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    try {
      await api.put(`/notifications/inbox/${id}`, { is_read: true });
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.debug('Mark read failed:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.action_url) {
      navigate(notification.action_url);
    }
    setOpen(false);
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'agentic_pm':
        return <Brain className="w-4 h-4" />;
      case 'governance':
        return <Shield className="w-4 h-4" />;
      case 'compliance':
        return <FileCheck className="w-4 h-4" />;
      case 'delivery':
        return <Package className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'agentic_pm':
        return 'bg-purple-100 text-purple-700';
      case 'governance':
        return 'bg-blue-100 text-blue-700';
      case 'compliance':
        return 'bg-green-100 text-green-700';
      case 'delivery':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Notifications"
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-[var(--clr-brand-primary)]" />
        ) : (
          <Bell className="w-5 h-5 text-gray-600" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
            <span className="font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <span className="text-xs text-[var(--clr-brand-primary)] font-medium">
                {unreadCount} new
              </span>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-[320px] overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center text-gray-500">
                <div className="w-5 h-5 border-2 border-[var(--clr-brand-primary)] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${
                    !notification.is_read ? 'bg-blue-50/50' : ''
                  }`}
                  data-testid={`notification-item-${notification.id}`}
                >
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getSourceColor(notification.source)}`}>
                    {getSourceIcon(notification.source)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {notification.created_at ? new Date(notification.created_at).toLocaleString() : ''}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notification.is_read && (
                      <button
                        onClick={(e) => handleMarkRead(notification.id, e)}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="Mark as read"
                      >
                        <Check className="w-4 h-4 text-gray-500" />
                      </button>
                    )}
                    {notification.action_url && (
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t bg-gray-50">
            <button
              onClick={() => {
                navigate('/notifications/inbox');
                setOpen(false);
              }}
              className="w-full text-center text-sm text-[var(--clr-brand-primary)] font-medium hover:underline"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
